import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { Camera, ScanLine, X, AlertCircle, Search, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

/**
 * AdminOrderSearch — scan or type an order # to instantly open it.
 *
 * Lives on the AdminOrders page top bar. Two input modes:
 *  - Manual entry: text input + Find button. Accepts any order number
 *    (uppercased + trimmed on submit so a Code-39 scanner that strips
 *    case-info still works).
 *  - Camera scan: opens the device's back camera and decodes Code-39
 *    (the format we print on invoices), Code-128, and QR.
 *
 * On match: calls `onFound(order)` with the matched order from the
 * already-loaded list (no extra network call). On miss: falls back to
 * a server-side lookup via the parent's `lookupRemote` callback so
 * older orders not on the current page still resolve.
 *
 * @param {{
 *   orders: Array<{ id:string, order_number:string }>,
 *   onFound: (order:object) => void,
 *   lookupRemote: (orderNumber:string) => Promise<object|null>,
 * }} props
 */
const AdminOrderSearch = ({ orders, onFound, lookupRemote }) => {
  const { toast } = useToast();
  const [value, setValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const controlsRef = useRef(null);

  // --- Camera lifecycle ---
  useEffect(() => {
    if (!scanning) return undefined;
    let cancelled = false;
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.CODE_39,
      BarcodeFormat.CODE_128,
      BarcodeFormat.QR_CODE,
    ]);
    const reader = new BrowserMultiFormatReader(hints);
    (async () => {
      try {
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } } },
          videoRef.current,
          (result) => {
            if (!result || cancelled) return;
            const text = (result.getText() || '').trim().toUpperCase();
            if (!text) return;
            setValue(text);
            stopScan();
            setTimeout(() => lookup(text), 150);
          },
        );
        controlsRef.current = controls;
      } catch (err) {
        if (cancelled) return;
        setScanError(
          err?.name === 'NotAllowedError'
            ? 'Camera blocked. Enable camera permission.'
            : 'Could not start camera. Use manual entry.',
        );
      }
    })();
    return () => {
      cancelled = true;
      stopScan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  const stopScan = () => {
    try { controlsRef.current?.stop?.(); } catch { /* ignore */ }
    controlsRef.current = null;
    setScanning(false);
  };

  const lookup = async (raw) => {
    const orderNumber = (raw ?? value).trim().toUpperCase();
    if (!orderNumber) {
      toast({ title: 'Enter an order number', description: 'e.g. MR-00000077' });
      return;
    }
    // First, look in the already-loaded list (zero network).
    const local = orders.find((o) =>
      (o.order_number || '').toUpperCase() === orderNumber,
    );
    if (local) {
      onFound(local);
      setValue('');
      return;
    }
    // Fall back to server-side lookup for older / paginated-out orders.
    setBusy(true);
    try {
      const remote = await lookupRemote(orderNumber);
      if (remote) {
        onFound(remote);
        setValue('');
      } else {
        toast({ title: 'Order not found', description: orderNumber });
      }
    } catch {
      toast({ title: 'Lookup failed', description: 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4" data-testid="admin-order-search">
      {scanning ? (
        <div className="relative rounded-xl overflow-hidden bg-black aspect-[16/7] max-w-2xl" data-testid="admin-order-search-scanner">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            muted
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-3/4 h-14 border-2 border-emerald-400 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
          </div>
          <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
            <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 text-[10px] font-bold text-white inline-flex items-center gap-1.5">
              <ScanLine className="w-3 h-3 animate-pulse" /> Point at invoice barcode
            </div>
            <button
              type="button"
              onClick={stopScan}
              className="bg-white/90 hover:bg-white text-slate-900 rounded-full p-1.5 transition"
              data-testid="admin-order-search-scanner-close"
              aria-label="Stop scanning"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {scanError && (
            <div className="absolute inset-x-3 bottom-3 bg-red-500/95 text-white text-xs font-semibold rounded-lg p-2 flex items-start gap-1.5">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {scanError}
            </div>
          )}
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap sm:flex-nowrap items-stretch">
          <div className="relative flex-1 min-w-0">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
              placeholder="Scan or type order # (MR-00000077)"
              className="w-full rounded-full border border-neutral-300 bg-white pl-9 pr-4 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
              data-testid="admin-order-search-input"
              autoComplete="off"
              spellCheck="false"
            />
          </div>
          <button
            type="button"
            onClick={() => { setScanError(null); setScanning(true); }}
            className="rounded-full border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 text-xs font-bold px-3 py-2 inline-flex items-center gap-1.5 transition"
            data-testid="admin-order-search-scan-btn"
            aria-label="Scan barcode"
          >
            <Camera className="w-4 h-4" /> Scan
          </button>
          <button
            type="button"
            onClick={() => lookup()}
            disabled={busy || !value.trim()}
            className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] disabled:bg-[#0078D4]/40 disabled:cursor-not-allowed text-white text-xs font-bold px-4 py-2 inline-flex items-center gap-1.5 transition"
            data-testid="admin-order-search-submit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Find
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminOrderSearch;
